import React, { useState } from "react";

const WhatsAppOnboardingModeModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [selectedMode, setSelectedMode] = useState("standard");

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm?.(selectedMode);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]"
        onClick={loading ? undefined : onClose}
        aria-label="Close dialog"
      />

      {/* Modal Dialog */}
      <div className="relative z-10 w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-black/5">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Connect WhatsApp Business
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Select how you would like to connect your WhatsApp number to the DocLift platform.
            </p>
          </div>
        </div>

        {/* Mode Options */}
        <div className="mt-5 space-y-3">
          {/* Option 1: Standard Onboarding */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-all ${
              selectedMode === "standard"
                ? "border-emerald-500 bg-emerald-50/40 shadow-sm ring-1 ring-emerald-500"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
            }`}
          >
            <input
              type="radio"
              name="onboarding_mode"
              value="standard"
              checked={selectedMode === "standard"}
              onChange={() => setSelectedMode("standard")}
              disabled={loading}
              className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-slate-300"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">
                  Connect a new WhatsApp number
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                  Standard Cloud API
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                Best for businesses connecting a new or dedicated phone number not currently active on the WhatsApp Business mobile app.
              </p>
            </div>
          </label>

          {/* Option 2: Coexistence Onboarding */}
          <label
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-all ${
              selectedMode === "coexistence"
                ? "border-emerald-500 bg-emerald-50/40 shadow-sm ring-1 ring-emerald-500"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
            }`}
          >
            <input
              type="radio"
              name="onboarding_mode"
              value="coexistence"
              checked={selectedMode === "coexistence"}
              onChange={() => setSelectedMode("coexistence")}
              disabled={loading}
              className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-slate-300"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">
                  Connect an existing WhatsApp Business App number
                </span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                  Coexistence (v4)
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                Allows your phone number to continue operating in the WhatsApp Business mobile app while simultaneously connecting to DocLift Cloud API.
              </p>
              <div className="mt-2.5 rounded-xl border border-amber-200 bg-amber-50/80 p-2.5 text-[11px] text-amber-800 leading-relaxed">
                <span className="font-semibold">Notice:</span> Intended for eligible existing WhatsApp Business App numbers. You must confirm connection from within the WhatsApp Business app and meet Meta&apos;s eligibility requirements.
              </div>
            </div>
          </label>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleConfirm}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
          >
            {loading ? (
              <>
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Starting Setup…
              </>
            ) : (
              "Continue with Meta"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppOnboardingModeModal;
